import { PreparationIdentity } from './components/PreparationIdentity';
import React, { useEffect } from 'react';

import {
  getRelationshipTypeLabel,
  getRerunReasonLabel,
} from '../../utils/derivedRunDraft';
import { formatRunTimestamp } from '../../utils/runFormatting';
import {
  formatSavedRunChangeSummary,
  getSavedRunArtifacts,
  getSavedRunExecutionSetup,
  getSavedRunFailureMessage,
  getSavedRunIdentity,
  getSavedRunIdentitySummary,
  getSavedRunOfficialSummary,
  getSavedRunParams,
  getSavedRunOperationalState,
  getSavedRunProjectSample,
  getSavedRunRelationshipType,
  getSavedRunRequestedTraces,
  getSavedRunParamsChanged,
  getSavedRunRerunReasonCode,
  getSavedRunRerunReasonText,
  getSavedRunSampleLabel,
  getSavedRunSourceRunId,
  getSavedRunStatus,
  getSavedRunTraceChanged,
  getSavedRunValidity,
  getSavedRunEffectiveTraces,
  getSavedRunPipeline,
  getSavedRunMeasurementInputs,
} from '../../utils/savedRunDetail';
import { EncapsulationRunView } from './EncapsulationRunView';
import { SizeRunView } from './SizeRunView';
import { SizeReportActions } from './SizeReportActions';

const SAVED_RUN_NOTE =
  'Saved runs are immutable. Explore changes from this batch run to preview or save a derived run.';

function getStatusClass(status) {
  if (status === 'running') return 'text-yellow-600';
  if (status === 'completed') return 'text-green-600';
  if (status === 'failed') return 'text-red-600';
  if (status === 'preview') return 'text-sky-600';
  return 'text-slate-600';
}

function JsonCard({ title, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      <pre className="mt-3 overflow-x-auto rounded bg-slate-50 p-3 text-xs text-slate-700">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function TraceSummaryTable({ title, traces }) {
  const rows = Array.isArray(traces) ? traces : [];

  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      {!rows.length ? (
        <p className="mt-3 text-sm text-slate-500">No trace data recorded.</p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Input</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Trace</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Wavelength</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Signal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {rows.map((trace) => (
                <tr key={`${title}-${trace.import_id}`}>
                  <td className="px-3 py-2 text-slate-700">
                    <div className="font-medium">Import {trace.import_id}</div>
                    <div className="text-xs text-slate-500">
                      {trace.filename || trace.file_role || 'Frozen provenance input'}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{trace.trace_id || '—'}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {trace.wavelength_nm != null ? `${trace.wavelength_nm} nm` : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-500">
                    {trace.signal_unit_canonical || trace.signal_unit_raw || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RunSummary({ run, onOpenRun }) {
  const pipeline = getSavedRunPipeline(run);
  const sampleName = getSavedRunSampleLabel(run);
  const status = getSavedRunStatus(run);
  const relationshipType = getSavedRunRelationshipType(run);
  const sourceRunId = getSavedRunSourceRunId(run);
  const rerunReasonCode = getSavedRunRerunReasonCode(run);
  const rerunReasonText = getSavedRunRerunReasonText(run);
  const identity = getSavedRunIdentity(run);
  const identitySummary = getSavedRunIdentitySummary(run);
  const paramsChanged = getSavedRunParamsChanged(run);
  const traceChanged = getSavedRunTraceChanged(run);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Saved run
            </p>
            <h3 className="mt-1 text-xl font-semibold text-slate-900">
              {pipeline} • {getRelationshipTypeLabel(relationshipType)}
            </h3>
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-700">
              <div>
                <span className="font-medium text-slate-900">Sample:</span>{' '}
                {sampleName}
              </div>
              <div>
                <span className="font-medium text-slate-900">Status:</span>{' '}
                <span className={`font-medium capitalize ${getStatusClass(status)}`}>
                  {status || '—'}
                </span>
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              Created {formatRunTimestamp(identity?.created_at)} • Finished{' '}
              {formatRunTimestamp(identity?.finished_at)}
            </p>
            <p className="mt-3 max-w-3xl text-sm text-slate-700">
              <PreparationIdentity identity={identity} /><br />
              {identitySummary}
            </p>
          </div>
          <div className="grid gap-2 text-sm text-slate-700">
            <div>
              <span className="font-medium text-slate-900">Run type:</span>{' '}
              {getRelationshipTypeLabel(relationshipType)}
            </div>
            <div>
              <span className="font-medium text-slate-900">Validity:</span>{' '}
              {getSavedRunValidity(run)}
            </div>
            <div>
              <span className="font-medium text-slate-900">Params changed:</span>{' '}
              {paramsChanged == null ? '—' : paramsChanged ? 'Yes' : 'No'}
            </div>
            <div>
              <span className="font-medium text-slate-900">Trace changed:</span>{' '}
              {traceChanged == null ? '—' : traceChanged ? 'Yes' : 'No'}
            </div>
            <div>
              <span className="font-medium text-slate-900">Execution diff:</span>{' '}
              {formatSavedRunChangeSummary(run)}
            </div>
            {sourceRunId && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-900">Source run:</span>
                <span className="font-mono text-xs text-slate-600">{sourceRunId}</span>
                {onOpenRun && (
                  <button
                    type="button"
                    onClick={() => onOpenRun(sourceRunId)}
                    className="text-sm font-medium text-blue-600 hover:underline"
                  >
                    Open source run
                  </button>
                )}
              </div>
            )}
            {rerunReasonCode && (
              <div>
                <span className="font-medium text-slate-900">Reason:</span>{' '}
                {getRerunReasonLabel(rerunReasonCode)}
                {rerunReasonText ? ` • ${rerunReasonText}` : ''}
              </div>
            )}
          </div>
      </div>
      <p className="mt-4 border-t border-slate-200 pt-3 text-xs text-slate-500">
        {SAVED_RUN_NOTE}
      </p>
    </div>
  );
}

function renderPipelineView(run, params) {
  const pipeline = getSavedRunPipeline(run);
  const artifacts = getSavedRunArtifacts(run);
  const officialSummary = getSavedRunOfficialSummary(run) || {};
  const status = getSavedRunStatus(run);
  const sampleName = getSavedRunSampleLabel(run);
  const failureMessage = getSavedRunFailureMessage(run);

  if (pipeline === 'encapsulation') {
    return (
      <EncapsulationRunView
        sampleName={sampleName}
        showSampleSummary={false}
        executionSetup={getSavedRunExecutionSetup(run)}
        status={status}
        encapsulationEfficiency={officialSummary.encapsulation_efficiency ?? null}
        totalRegion={artifacts.encapsulation_region_total ?? null}
        totalFit={artifacts.encapsulation_fitted_total ?? null}
        freeRegion={artifacts.encapsulation_region_free ?? null}
        freeFit={artifacts.encapsulation_fitted_free ?? null}
        failureMessage={failureMessage}
        params={params}
      />
    );
  }

  if (pipeline === 'size') {
    return (
      <SizeRunView
        sampleName={sampleName}
        showSampleSummary={false}
        status={status}
        components={officialSummary.components || []}
        messages={artifacts.size_messages || []}
        plots={artifacts.size_plots || null}
        failureMessage={failureMessage}
        params={params}
      />
    );
  }

  return (
    <div className="p-6">
      <h3 className="text-lg font-semibold mb-4">
        Unsupported pipeline: {pipeline}
      </h3>

      <pre className="rounded bg-slate-50 p-4 text-xs overflow-x-auto">
        {JSON.stringify(run, null, 2)}
      </pre>
    </div>
  );
}

export function RunAnalysisView({ run, onOpenRun }) {
  useEffect(() => {
    console.log('[RunAnalysisView lifecycle] mount', {
      runId: getSavedRunIdentity(run)?.run_id ?? null,
      sourceRunId: getSavedRunSourceRunId(run),
      measurementInputCount: getSavedRunMeasurementInputs(run).length,
      projectSampleId: getSavedRunProjectSample(run)?.sample_id ?? null,
    });
    return () => {
      console.log('[RunAnalysisView lifecycle] unmount', {
        runId: getSavedRunIdentity(run)?.run_id ?? null,
        sourceRunId: getSavedRunSourceRunId(run),
        measurementInputCount: getSavedRunMeasurementInputs(run).length,
        projectSampleId: getSavedRunProjectSample(run)?.sample_id ?? null,
      });
    };
  }, []);

  const params = getSavedRunParams(run);
  const projectSample = getSavedRunProjectSample(run);
  const requestedTraces = getSavedRunRequestedTraces(run);
  const effectiveTraces = getSavedRunEffectiveTraces(run);
  const identity = getSavedRunIdentity(run);
  const pipeline = getSavedRunPipeline(run);
  const status = getSavedRunStatus(run);
  const isValid = getSavedRunOperationalState(run)?.is_valid === true;

  return (
    <div className="space-y-6">
      <RunSummary
        run={run}
        onOpenRun={onOpenRun}
      />
      <SizeReportActions
        key={identity?.run_id || 'missing-run-id'}
        workflowRunId={identity?.run_id || null}
        pipeline={pipeline}
        status={status}
        isValid={isValid}
      />
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <TraceSummaryTable title="Requested traces" traces={requestedTraces} />
        <TraceSummaryTable title="Effective traces" traces={effectiveTraces} />
      </div>
      {renderPipelineView(run, params)}
    </div>
  );
}
